import makeStyles from '@mui/styles/makeStyles';
import classNames from "classnames";
import {isEmpty} from 'lodash';
import React from "react";
import {useTranslation} from "react-i18next";
import {formatPhoneNumber, initials} from "common/helpers";
import {color, font, radius, type} from "common/tokens";
import Card from "components/redesign/Card";
import ShimmerImage from "components/redesign/ShimmerImage";

const useStyles = makeStyles(theme => ({
  card: {
    width: '100%',
  },
  accent: {
    height: 8,
    background: color.ACCENT,
  },
  accentPartner: {
    background: color.PARTNER,
  },
  body: {
    display: 'flex',
    alignItems: 'center',
    gap: 24,
    padding: 32,
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'flex-start',
      padding: 20,
      gap: 16,
    },
  },
  avatarOuter: {
    width: 120,
    height: 120,
    borderRadius: radius.PILL,
    border: `2px solid ${color.DIVIDER}`,
    background: color.ACCENT_TINT,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  avatarOuterPartner: {
    background: color.PARTNER_TINT,
  },
  avatarInner: {
    width: 104,
    height: 104,
    borderRadius: radius.PILL,
  },
  avatarInitials: {
    ...type.panelTitle,
    color: color.TEXT_MUTED,
  },
  info: {
    flex: '1 1 0',
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  eyebrow: {
    ...type.eyebrow,
    fontSize: 14,
    fontWeight: font.BOLD,
    color: color.PURPLE,
    margin: 0,
  },
  name: {
    fontFamily: font.SANS,
    fontSize: 28,
    lineHeight: '34px',
    letterSpacing: '-0.5px',
    fontWeight: font.BOLD,
    color: color.TEXT,
    margin: 0,
    wordBreak: 'break-word',
  },
  row: {
    ...type.meta,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    color: color.TEXT_SUBTLE,
    margin: 0,
  },
  rowValue: {
    fontWeight: font.SEMIBOLD,
    color: color.TEXT_MUTED,
  },
  bio: {
    ...type.body,
    color: color.TEXT_MUTED,
    margin: '4px 0 0',
  },
  action: {
    flexShrink: 0,
  },
}));

// A picture the user has removed comes back from the API as the string "null",
// which is not empty and would render as a broken image.
const pictureOf = picture => (isEmpty(picture) || picture === 'null' ? '' : picture);

const ProfileCard = ({
  eyebrow,
  name,
  memberId,
  picture,
  email,
  phoneNumber,
  bio,
  isSelf = false,
  action,
}) => {
  const classes = useStyles();
  const {t} = useTranslation();
  const photo = pictureOf(picture);

  return (
    <Card variant={'media'} className={classes.card}>
      <div className={classNames(classes.accent, !isSelf && classes.accentPartner)}/>
      <div className={classes.body}>
        <span className={classNames(classes.avatarOuter, !isSelf && classes.avatarOuterPartner)}>
          {isEmpty(photo)
            ? <span className={classes.avatarInitials}>{initials(name)}</span>
            : <ShimmerImage src={photo} className={classes.avatarInner}/>}
        </span>

        <div className={classes.info}>
          {Boolean(eyebrow) && <p className={classes.eyebrow}>{eyebrow}</p>}
          <p className={classes.name}>{name}</p>
          {Boolean(memberId) && (
            <p className={classes.row}>
              {`${t('memberId')}:`}
              <span className={classes.rowValue}>{memberId}</span>
            </p>
          )}
          {Boolean(email) && (
            <p className={classes.row}>
              {`${t('email')}:`}
              <span className={classes.rowValue}>{email}</span>
            </p>
          )}
          {Boolean(phoneNumber) && (
            <p className={classes.row}>
              {`${t('phoneNumber')}:`}
              <span className={classes.rowValue}>{formatPhoneNumber(phoneNumber)}</span>
            </p>
          )}
          {Boolean(bio) && <p className={classes.bio}>{bio}</p>}
        </div>

        {Boolean(action) && <div className={classes.action}>{action}</div>}
      </div>
    </Card>
  );
};

export default ProfileCard;
